/**
 * This utility borrows heavily from https://code.amazon.com/packages/MeridianComponents/blobs/4.x/--/src/utils/is-element-of.js
 * Credit goes to Elliot Dickison<elliotdi@amazon.com>
 */

/**
 * Determines if two components are the same. Uses some tricks to smooth out
 * wrinkles with react-hot-loader in development mode
 *
 * console.log(isSameComponent(Checkbox, Checkbox)) // true
 * console.log(isSameComponent(Checkbox, Radio)) // false
 */

const isObjectOrFunction = objOrFunc => typeof objOrFunc === 'object' || typeof objOrFunc === 'function';
function hasMember(type, obj) {
  return isObjectOrFunction(obj) && type in obj;
}
const isSameComponent = (alpha, beta) => {
  if (process.env.NODE_ENV !== 'production' && hasMember('name', alpha) && hasMember('name', beta) && (alpha.name || beta.name)) {
    // If a name is set then rely on that in development mode. The component
    // name is more reliable than the type in development mode because of a
    // limitation of react-hot-loader (see:
    // https://github.com/gaearon/react-hot-loader/issues/304). Don't use this
    // in production though because minification will change the name and break
    // the comparison.

    const aName = hasMember('displayName', alpha) ? alpha.displayName : alpha.name;
    const bName = hasMember('displayName', beta) ? beta.displayName : beta.name;
    return aName === bName;
  }
  // Just check if the components reference the same class/function.
  return alpha === beta;
};
/**
 * Determines if an element is an instance of a particular component.
 *
 * const isCheckbox = isElementOf(Checkbox)
 * console.log(isCheckbox(<Checkbox />)) // true
 */
const isElementOf = component => element => {
  if (!component || !element || typeof element === 'string') {
    // Bail if either the component or element wasn't provided
    return false;
  }
  if (hasMember('type', element)) {
    const elementType = element.type;

    /**
     * Check for styled-components
     * Styled-components typically add a __emotion_base or target property to the wrapped component,
     * which refers to the original component.
     */
    // eslint-disable-next-line no-underscore-dangle
    if (elementType.styledComponentId && (elementType.target || elementType.__emotion_base)) {
      // eslint-disable-next-line no-underscore-dangle
      const baseComponent = elementType.target || elementType.__emotion_base;
      return isSameComponent(baseComponent, component);
    }
    if (hasMember('WrappedComponent', element.type) && element.type.WrappedComponent) {
      // If the element's component has a static WrappedComponent property, assume
      // we're working with a HoC and compare the wrapped component instead of the
      // HoC component. The connect HoC from redux uses this pattern, so this
      // check ensures that isElementOf works correctly on redux-connected
      // components.

      return isSameComponent(element.type.WrappedComponent, component) || isSameComponent(element.type.WrappedComponent, component.WrappedComponent);
    }
    // If WrappedComponent isn't set do a normal comparison of element.type
    // (the component that element was instantiated from) and component.
    return isSameComponent(element.type, component);
  }
  return false;
};

export { isElementOf as i };
//# sourceMappingURL=isElementOf-Wg8aEFDZ.js.map
